// src/services/httpService.ts
import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosInstance from './interceptors';
import Router from "@/router";
import { message } from "ant-design-vue";

// 使用环境变量中的API地址
const baseURL = import.meta.env.PROD ? '' : import.meta.env.VITE_API_URL;

// 定义响应数据格式
interface ApiResponse<T = any> {
  code: number;
  data: T;
  message: string;
  [key: string]: any;
}

// 请求方法类型
type Method = 'get' | 'post' | 'put' | 'delete' | 'patch';

// 封装请求方法
const request = async <T>(
    method: Method,
    url: string,
    data?: any,
    params?: any,
    config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      method,
      url: url.startsWith('http') ? url : baseURL + url,
      data,
      params,
      timeout: config?.timeout || 30000,
      ...config
    };

    const response: AxiosResponse<ApiResponse<T>> = await axiosInstance.request(requestConfig);

    // 直接返回完整响应数据，让 dashboard.js 的 handleResponse 处理
    return response.data as T;  // 返回 {code, data, message}

  } catch (error: any) {
    // 错误处理保持不变
    if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请稍后重试');
      throw new Error('请求超时');
    }
    throw error;
  }
};

// 导出请求方法（保持原有接口）
export const get = <T>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> =>
    request<T>('get', url, null, params, config);

export const post = <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> =>
    request<T>('post', url, data, null, config);

export const put = <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> =>
    request<T>('put', url, data, null, config);

export const del = <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    request<T>('delete', url, null, null, config);

export const patch = <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> =>
    request<T>('patch', url, data, null, config);

export default {
  get,
  post,
  put,
  del,
  patch,
  axiosInstance
};